import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import {
  RefreshCw,
  Plus,
  Package,
  History,
  ArrowRight,
  Warehouse,
  AlertTriangle,
  Clock,
  Undo2,
  Trash2,
  WifiOff,
  ScanBarcode
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { offlineApi } from "../lib/syncManager";
import { Badge } from "../components/ui/badge";
import BarcodeScanner from "../components/BarcodeScanner";

// Helper to show notification summary after refill
const showRefillSummary = async (refillInfo) => {
  try {
    const res = await axios.get(`${API}/notifications/summary`);
    const { expiring_products, low_stock_products } = res.data;
    
    // Show main refill toast
    toast.success(
      <div>
        <div className="font-medium">Auffüllung erfolgreich</div>
        <div className="text-sm mt-1">
          <span className="font-medium">Fach {refillInfo.slot_id}:</span> {refillInfo.product_name}
        </div>
        <div className="text-sm text-slate-500">
          {refillInfo.old_stock} → {refillInfo.new_stock} Stück (+{refillInfo.quantity})
        </div>
      </div>,
      { duration: 5000 }
    );
    
    // Show expiring products warning
    if (expiring_products && expiring_products.length > 0) {
      const expired = expiring_products.filter(p => p.status === 'expired');
      const critical = expiring_products.filter(p => p.status === 'critical');
      const warning = expiring_products.filter(p => p.status === 'warning');
      
      if (expired.length > 0) {
        toast.error(
          <div>
            <div className="font-medium flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" />
              {expired.length} Produkt(e) ABGELAUFEN!
            </div>
            <ul className="text-sm mt-1">
              {expired.slice(0, 3).map((p, i) => (
                <li key={i}>• {p.product_name}</li>
              ))}
            </ul>
          </div>,
          { duration: 8000 }
        );
      }
      
      if (critical.length > 0) {
        toast.warning(
          <div>
            <div className="font-medium flex items-center gap-2">
              <Clock className="w-4 h-4" />
              {critical.length} Produkt(e) laufen bald ab!
            </div>
            <ul className="text-sm mt-1">
              {critical.slice(0, 3).map((p, i) => (
                <li key={i}>• {p.product_name} ({p.days_until} Tage)</li>
              ))}
            </ul>
          </div>,
          { duration: 6000 }
        );
      }
      
      if (warning.length > 0 && expired.length === 0 && critical.length === 0) {
        toast.info(
          <div>
            <div className="font-medium">{warning.length} Produkt(e) laufen in 30 Tagen ab</div>
            <div className="text-sm text-slate-500">Siehe Produkte-Seite für Details</div>
          </div>,
          { duration: 4000 }
        );
      }
    }
    
    // Show low stock warning
    if (low_stock_products && low_stock_products.length > 0) {
      toast.info(
        <div>
          <div className="font-medium">{low_stock_products.length} Produkt(e) mit niedrigem Bestand</div>
          <ul className="text-sm mt-1">
            {low_stock_products.slice(0, 3).map((p, i) => (
              <li key={i}>• {p.product_name}: {p.current_stock} Stück</li>
            ))}
          </ul>
        </div>,
        { duration: 5000 }
      );
    }
  } catch (err) {
    // Silently fail - the main refill was successful
    console.error("Failed to fetch notification summary", err);
  }
};

const RefillForm = ({ compartments, products, onSuccess }) => {
  const [compartmentId, setCompartmentId] = useState("");
  const [quantity, setQuantity] = useState("");
  const [source, setSource] = useState("warehouse");
  const [saving, setSaving] = useState(false);
  const [scannerOpen, setScannerOpen] = useState(false);

  const selectedCompartment = compartments.find(c => c.id === compartmentId);
  const product = selectedCompartment 
    ? products.find(p => p.id === selectedCompartment.product_id)
    : null;

  // Handle barcode scan - find product and select compartment
  const handleBarcodeScan = async (code) => {
    try {
      const res = await axios.get(`${API}/products/by-ean/${code}`);
      if (res.data) {
        // Find compartment with this product
        const comp = compartments.find(c => c.product_id === res.data.id);
        if (comp) {
          setCompartmentId(comp.id);
          toast.success(`Produkt gefunden: ${res.data.name} (Fach ${comp.slot_id})`);
        } else {
          toast.warning(`Produkt "${res.data.name}" gefunden, aber keinem Fach zugewiesen`);
        }
      }
    } catch (err) {
      toast.error(`Kein Produkt mit EAN ${code} gefunden`);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!compartmentId || !quantity || parseInt(quantity) <= 0) {
      toast.error("Bitte alle Felder ausfüllen");
      return;
    }

    if (!product) {
      toast.error("Dieses Fach hat kein zugewiesenes Produkt");
      return;
    }

    const oldStock = selectedCompartment.current_stock;
    const newStock = Math.min(oldStock + parseInt(quantity), selectedCompartment.max_capacity);

    setSaving(true);
    try {
      await axios.post(`${API}/refills`, {
        compartment_id: compartmentId,
        product_id: product.id,
        product_name: product.name,
        slot_id: selectedCompartment.slot_id,
        quantity: parseInt(quantity),
        refill_type: "add",
        source
      });
      
      // Show summary with warnings
      await showRefillSummary({
        slot_id: selectedCompartment.slot_id,
        product_name: product.name,
        old_stock: oldStock,
        new_stock: newStock,
        quantity: parseInt(quantity)
      });
      
      setCompartmentId("");
      setQuantity("");
      onSuccess();
    } catch (err) {
      toast.error("Fehler beim Auffüllen");
    } finally {
      setSaving(false);
    }
  };

  // Filter compartments that have a product assigned and sort by slot_id
  const filledCompartments = compartments
    .filter(c => c.product_id)
    .sort((a, b) => a.slot_id.localeCompare(b.slot_id));

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label>Fach auswählen *</Label>
        <div className="flex gap-2">
          <Select value={compartmentId} onValueChange={setCompartmentId}>
            <SelectTrigger data-testid="refill-compartment-select" className="flex-1">
              <SelectValue placeholder="Fach wählen" />
            </SelectTrigger>
            <SelectContent>
              {filledCompartments.map((c) => {
                const prod = products.find(p => p.id === c.product_id);
                const displayName = c.custom_name || c.slot_id;
                return (
                  <SelectItem key={c.id} value={c.id}>
                    {displayName} - {prod?.name || "Unbekannt"} ({c.current_stock}/{c.max_capacity})
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
          <Button
            type="button"
            variant="outline"
            onClick={() => setScannerOpen(true)}
            data-testid="refill-scan-btn"
            title="Produkt per Barcode scannen"
          >
            <ScanBarcode className="w-5 h-5" />
          </Button>
        </div>
      </div>

      {/* Barcode Scanner */}
      <BarcodeScanner
        isOpen={scannerOpen}
        onClose={() => setScannerOpen(false)}
        onScan={handleBarcodeScan}
        title="Produkt per Barcode scannen"
      />

      {selectedCompartment && product && (
        <div className="bg-slate-50 rounded-md p-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="font-medium">{product.name}</div>
              <div className="text-sm text-slate-500">
                Aktuell: {selectedCompartment.current_stock} / {selectedCompartment.max_capacity}
              </div>
            </div>
            <div className="text-right">
              <div className="text-lg font-heading font-bold text-rose-600">
                {product.price.toFixed(2)}€
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Menge *</Label>
          <Input
            type="number"
            min="1"
            value={quantity}
            onChange={(e) => setQuantity(e.target.value)}
            placeholder="z.B. 5"
            data-testid="refill-quantity-input"
          />
        </div>
        <div className="space-y-2">
          <Label>Quelle</Label>
          <Select value={source} onValueChange={setSource}>
            <SelectTrigger data-testid="refill-source-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="warehouse">Aus Lager</SelectItem>
              <SelectItem value="direct">Direktlieferung</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <Button 
        type="submit" 
        disabled={saving || !compartmentId || !quantity}
        className="w-full bg-rose-600 hover:bg-rose-700"
        data-testid="refill-submit-btn"
      >
        {saving ? "Auffüllen..." : "Auffüllen"}
      </Button>
    </form>
  );
};

const RefillHistoryItem = ({ refill, onUndo }) => {
  const [undoing, setUndoing] = useState(false);

  const handleUndo = async () => {
    setUndoing(true);
    try {
      await axios.delete(`${API}/refills/${refill.id}`);
      toast.success("Auffüllung rückgängig gemacht");
      onUndo();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler beim Rückgängigmachen");
    } finally {
      setUndoing(false);
    }
  };

  return (
    <div className="flex items-center gap-4 py-3 border-b border-slate-100 last:border-0">
      <div className="w-10 h-10 rounded-md bg-emerald-100 flex items-center justify-center">
        <Plus className="w-5 h-5 text-emerald-600" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-mono text-sm bg-slate-100 px-2 py-0.5 rounded">
            {refill.slot_id}
          </span>
          <span className="font-medium truncate">{refill.product_name}</span>
        </div>
        <div className="text-sm text-slate-500 flex items-center gap-2 mt-1">
          <span>+{refill.quantity} Stück</span>
          <span>·</span>
          <span className="flex items-center gap-1">
            {refill.source === "warehouse" ? (
              <><Warehouse className="w-3 h-3" /> Aus Lager</>
            ) : (
              "Direktlieferung"
            )}
          </span>
          {refill.user_name && (
            <>
              <span>·</span>
              <span className="text-slate-400">{refill.user_name}</span>
            </>
          )}
        </div>
      </div>
      <div className="text-right mr-2">
        <div className="text-sm text-slate-500">
          {new Date(refill.created_at).toLocaleDateString('de-DE')}
        </div>
        <div className="text-xs text-slate-400">
          {new Date(refill.created_at).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}
        </div>
      </div>
      <AlertDialog>
        <AlertDialogTrigger asChild>
          <Button 
            variant="ghost" 
            size="icon" 
            className="text-slate-400 hover:text-rose-600"
            data-testid={`undo-refill-${refill.id}`}
          >
            <Undo2 className="w-4 h-4" />
          </Button>
        </AlertDialogTrigger>
        <AlertDialogContent className="max-w-[95vw] sm:max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-base">Rückgängig machen?</AlertDialogTitle>
            <AlertDialogDescription className="text-sm">
              {refill.product_name} ({refill.slot_id}) - {refill.quantity}x
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleUndo}
              disabled={undoing}
              className="bg-[#DC2626] hover:bg-[#B91C1C]"
            >
              {undoing ? "..." : "Rückgängig"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

const RefillPage = () => {
  const [compartments, setCompartments] = useState([]);
  const [products, setProducts] = useState([]);
  const [refills, setRefills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isOffline, setIsOffline] = useState(!navigator.onLine);

  useEffect(() => {
    fetchData();
    
    // Online/offline listeners
    const handleOnline = () => setIsOffline(false);
    const handleOffline = () => setIsOffline(true);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const fetchData = async () => {
    try {
      // Use offline-capable API
      const [compRes, prodRes, refillRes] = await Promise.all([
        offlineApi.getCompartments(),
        offlineApi.getProducts(),
        offlineApi.getRefills()
      ]);
      setCompartments(compRes.data);
      setProducts(prodRes.data);
      setRefills(refillRes.data);
      
      // Show offline indicator if data from cache
      if (compRes.fromCache || prodRes.fromCache) {
        toast.info('Offline-Daten geladen', { duration: 2000 });
      }
    } catch (err) {
      toast.error("Fehler beim Laden");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-rose-600" />
      </div>
    );
  }

  // Find low stock compartments
  const lowStockCompartments = compartments
    .filter(c => c.product_id && (c.current_stock / c.max_capacity) < 0.3)
    .sort((a, b) => (a.current_stock / a.max_capacity) - (b.current_stock / b.max_capacity));

  return (
    <div className="space-y-6" data-testid="refill-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-heading font-bold text-slate-900 tracking-tight">
            Auffüllen
          </h1>
          <p className="text-slate-500 mt-1">
            Automaten-Fächer mit Produkten befüllen
          </p>
        </div>
        {isOffline && (
          <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-300">
            <WifiOff className="w-3 h-3 mr-1" />
            Offline-Modus
          </Badge>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Refill Form */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="font-heading flex items-center gap-2">
                <RefreshCw className="w-5 h-5" />
                Fach auffüllen
              </CardTitle>
            </CardHeader>
            <CardContent>
              {compartments.filter(c => c.product_id).length > 0 ? (
                <RefillForm 
                  compartments={compartments}
                  products={products}
                  onSuccess={fetchData}
                />
              ) : (
                <div className="text-center py-8">
                  <Package className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                  <p className="text-slate-500">
                    Keine Fächer mit Produkten vorhanden
                  </p>
                  <p className="text-sm text-slate-400 mt-1">
                    Weisen Sie zuerst Produkte zu Fächern zu
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>

        {/* Low Stock Alert */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.1 }}
        >
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="font-heading flex items-center gap-2 text-amber-600">
                <Package className="w-5 h-5" />
                Nachfüllen empfohlen ({lowStockCompartments.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {lowStockCompartments.length > 0 ? (
                <div className="space-y-2">
                  {lowStockCompartments.slice(0, 5).map((comp) => {
                    const product = products.find(p => p.id === comp.product_id);
                    const percent = Math.round((comp.current_stock / comp.max_capacity) * 100);
                    const displayName = comp.custom_name || comp.slot_id;
                    return (
                      <div 
                        key={comp.id}
                        className="flex items-center gap-3 p-3 bg-amber-50 border border-amber-200 rounded-md"
                      >
                        <span className="font-mono text-sm font-bold text-amber-700">
                          {displayName}
                        </span>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium truncate">
                            {product?.name || "Unbekannt"}
                          </div>
                          <div className="text-sm text-amber-700">
                            {comp.current_stock} / {comp.max_capacity} ({percent}%)
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  {lowStockCompartments.length > 5 && (
                    <p className="text-sm text-slate-500 text-center pt-2">
                      +{lowStockCompartments.length - 5} weitere Fächer
                    </p>
                  )}
                </div>
              ) : (
                <div className="text-center py-8">
                  <div className="w-12 h-12 mx-auto bg-emerald-100 rounded-full flex items-center justify-center mb-4">
                    <Package className="w-6 h-6 text-emerald-600" />
                  </div>
                  <p className="text-emerald-600 font-medium">
                    Alle Fächer gut gefüllt!
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Refill History */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.2 }}
      >
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="font-heading flex items-center gap-2">
              <History className="w-5 h-5" />
              Auffüll-Protokoll
            </CardTitle>
          </CardHeader>
          <CardContent>
            {refills.length > 0 ? (
              <div className="divide-y divide-slate-100">
                {refills.map((refill) => (
                  <RefillHistoryItem key={refill.id} refill={refill} onUndo={fetchData} />
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <History className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                <p className="text-slate-500">Noch keine Auffüllungen protokolliert</p>
              </div>
            )}
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
};

export default RefillPage;
